export type SubscriptionPlan = 'lite' | 'pro' | 'max';
export type BillingPlan = 'free' | SubscriptionPlan;
export type BillingCycle = 'monthly' | 'quarterly' | 'annual';
export type ChooserSource = 'paywall' | 'avatar' | 'profile' | 'pricing' | 'ai-gate' | 'github-gate';

export interface SubscriptionPlanContext {
	billingPlan: BillingPlan;
	promotionalAccessPlan: SubscriptionPlan | null;
	effectiveAccessPlan: BillingPlan;
	temporaryAccess: { tiers: Array<{ plan: SubscriptionPlan; endsAt: string }>; currentTierEndsAt: string; finalEndsAt: string } | null;
}

export type SubscriptionChooserContext =
	| { kind: 'visitor'; source: ChooserSource; requiredPlan?: SubscriptionPlan }
	| {
		kind: 'authenticated';
		source: ChooserSource;
		requiredPlan?: SubscriptionPlan;
		crossCycleUpgrade?: boolean;
		planContext: SubscriptionPlanContext;
		billingSubscription: {
			providerSubscriptionId?: string | null;
			billingCycle: BillingCycle | null;
			currentPeriodEnd: string | null;
			cancelAtPeriodEnd: boolean;
		};
	};

export interface PlanCatalogEntry {
	code: SubscriptionPlan;
	monthlyCents: number;
	quarterlyCents: number;
	annualCents: number;
}

export interface SubscriptionChooserState {
	selectedPlan: SubscriptionPlan;
	billingCycle: BillingCycle;
	cycles: Array<{ cycle: BillingCycle; disabled: boolean; reason?: string }>;
	cards: Array<{
		plan: SubscriptionPlan;
		action: 'signup' | 'checkout' | 'upgrade' | 'current' | 'cycle_preview_only' | 'unavailable';
		disabled: boolean;
		noImmediateAccessIncrease: boolean;
		disabledReason?: string;
	}>;
}

const plans: SubscriptionPlan[] = ['lite', 'pro', 'max'];
const cycles: BillingCycle[] = ['monthly', 'quarterly', 'annual'];
const rank: Record<BillingPlan, number> = { free: 0, lite: 1, pro: 2, max: 3 };
const cycleRank: Record<BillingCycle, number> = { monthly: 0, quarterly: 1, annual: 2 };

export function isSubscriptionUpgradeSelectionAllowed(input: {
	fromPlan: SubscriptionPlan;
	fromCycle: BillingCycle;
	toPlan: SubscriptionPlan;
	toCycle: BillingCycle;
	crossCycleUpgrade: boolean;
}): boolean {
	if (rank[input.toPlan] < rank[input.fromPlan] || cycleRank[input.toCycle] < cycleRank[input.fromCycle]) return false;
	if (input.toPlan === input.fromPlan && input.toCycle === input.fromCycle) return false;
	if (input.toCycle !== input.fromCycle && !input.crossCycleUpgrade) return false;
	return true;
}

export function buildSubscriptionChooserState(input: {
	context: SubscriptionChooserContext;
	plans: readonly PlanCatalogEntry[];
	selectedPlan?: SubscriptionPlan;
	billingCycle?: BillingCycle;
}): SubscriptionChooserState {
	const billingPlan = input.context.kind === 'authenticated' ? input.context.planContext.billingPlan : 'free';
	const preferredPlan = input.selectedPlan ?? defaultPlan(input.context, billingPlan);
	const trustedCycle = input.context.kind === 'authenticated' && billingPlan !== 'free' ? input.context.billingSubscription.billingCycle : null;
	const billingCycle = input.billingCycle && cycleAvailable(input.plans, input.billingCycle)
		? input.billingCycle
		: trustedCycle && cycleAvailable(input.plans, trustedCycle)
			? trustedCycle
			: firstAvailableCycle(input.plans, preferredPlan);
	const cards = plans.map((plan) => cardState(input.context, input.plans, plan, billingCycle));
	return {
		selectedPlan: preferredPlan,
		billingCycle,
		cycles: cycles.map((cycle) => cycleState(input.context, input.plans, cycle)),
		cards,
	};
}

function defaultPlan(context: SubscriptionChooserContext, billingPlan: BillingPlan): SubscriptionPlan {
	if (context.kind === 'visitor') return context.requiredPlan ?? 'pro';
	if (context.requiredPlan && rank[context.requiredPlan] > rank[billingPlan]) return context.requiredPlan;
	if (billingPlan !== 'free' && context.billingSubscription.billingCycle) return billingPlan;
	if (billingPlan === 'pro' || billingPlan === 'max') return 'max';
	return 'pro';
}

function firstAvailableCycle(catalog: readonly PlanCatalogEntry[], plan: SubscriptionPlan): BillingCycle {
	const entry = catalog.find((candidate) => candidate.code === plan);
	// A new checkout should lead with the normal value choice, while still
	// gracefully falling back when a provider product has not been configured.
	return (['quarterly', 'monthly', 'annual'] as const).find((cycle) => priceFor(entry, cycle) > 0) ?? 'monthly';
}

function cardState(context: SubscriptionChooserContext, catalog: readonly PlanCatalogEntry[], plan: SubscriptionPlan, cycle: BillingCycle) {
	const entry = catalog.find((candidate) => candidate.code === plan);
	const purchasable = priceFor(entry, cycle) > 0;
	const requiredPlan = context.requiredPlan;
	if (requiredPlan && rank[plan] < rank[requiredPlan]) {
		return {
			plan,
			action: 'unavailable' as const,
			disabled: true,
			noImmediateAccessIncrease: false,
			disabledReason: `${capitalize(requiredPlan)} or higher is required.`,
		};
	}
	if (context.kind === 'visitor') return { plan, action: purchasable ? 'signup' as const : 'unavailable' as const, disabled: !purchasable, noImmediateAccessIncrease: false };
	const billingPlan = context.planContext.billingPlan;
	const temporary = context.planContext.promotionalAccessPlan !== null;
	const noImmediateAccessIncrease = temporary && rank[context.planContext.effectiveAccessPlan] >= rank[plan];
	if (rank[plan] < rank[billingPlan]) {
		return {
			plan,
			action: 'unavailable' as const,
			disabled: true,
			noImmediateAccessIncrease,
			disabledReason: 'Plan downgrades are not available during an active subscription.',
		};
	}
	if (!purchasable) return { plan, action: 'unavailable' as const, disabled: true, noImmediateAccessIncrease };
	if (billingPlan === 'free') return { plan, action: 'checkout' as const, disabled: false, noImmediateAccessIncrease };
	const trustedCycle = context.billingSubscription.billingCycle;
	if (!trustedCycle) {
		// Public catalog surfaces never infer a member's cycle. They can still
		// offer a strictly higher tier as a safe hand-off to the paywall, where
		// the real subscription is read before any upgrade can proceed.
		if (isPublicCatalogSource(context.source) && rank[plan] > rank[billingPlan]) {
			return { plan, action: 'upgrade' as const, disabled: false, noImmediateAccessIncrease };
		}
		return { plan, action: 'unavailable' as const, disabled: true, noImmediateAccessIncrease };
	}
	if (cycle === trustedCycle && billingPlan === plan) return { plan, action: 'current' as const, disabled: true, noImmediateAccessIncrease };
	if (cycle !== trustedCycle && context.crossCycleUpgrade !== true) {
		return {
			plan,
			action: 'cycle_preview_only' as const,
			disabled: true,
			noImmediateAccessIncrease,
			disabledReason: 'Upgrades are currently available only on your existing billing cycle.',
		};
	}
	if (cycleRank[cycle] < cycleRank[trustedCycle]) {
		return {
			plan,
			action: 'unavailable' as const,
			disabled: true,
			noImmediateAccessIncrease,
			disabledReason: `The ${cycle} billing period is shorter than your current ${trustedCycle} period.`,
		};
	}
	return { plan, action: 'upgrade' as const, disabled: false, noImmediateAccessIncrease };
}

function isPublicCatalogSource(source: SubscriptionChooserContext['source']): boolean {
	return source === 'pricing' || source === 'ai-gate' || source === 'github-gate';
}

function cycleState(
	context: SubscriptionChooserContext,
	catalog: readonly PlanCatalogEntry[],
	cycle: BillingCycle,
): SubscriptionChooserState['cycles'][number] {
	if (!cycleAvailable(catalog, cycle)) return { cycle, disabled: true, reason: 'This billing period is not currently available.' };
	if (context.kind !== 'authenticated' || context.planContext.billingPlan === 'free' || context.crossCycleUpgrade !== true) return { cycle, disabled: false };
	const trustedCycle = context.billingSubscription.billingCycle;
	if (!trustedCycle) return { cycle, disabled: false };
	if (cycleRank[cycle] < cycleRank[trustedCycle]) {
		return { cycle, disabled: true, reason: `The ${cycle} billing period is shorter than your current ${trustedCycle} period.` };
	}
	const hasNondecreasingTarget = catalog.some(
		(entry) => rank[entry.code] >= rank[context.planContext.billingPlan] && priceFor(entry, cycle) > 0,
	);
	if (cycle !== trustedCycle && !hasNondecreasingTarget) {
		return { cycle, disabled: true, reason: 'This billing period is not currently available for a nondecreasing plan.' };
	}
	return { cycle, disabled: false };
}

function cycleAvailable(catalog: readonly PlanCatalogEntry[], cycle: BillingCycle): boolean {
	return catalog.some((entry) => priceFor(entry, cycle) > 0);
}

function priceFor(entry: PlanCatalogEntry | undefined, cycle: BillingCycle): number {
	if (!entry) return 0;
	return cycle === 'monthly' ? entry.monthlyCents : cycle === 'quarterly' ? entry.quarterlyCents : entry.annualCents;
}

function capitalize(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}
